import { useState, useEffect, useRef } from "react";

interface SacredBookProps {
  visible: boolean;
  onOpen: () => void;
}

export default function SacredBook({ visible, onOpen }: SacredBookProps) {
  const [hover, setHover] = useState(false);
  const [glowing, setGlowing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setGlowing(true), 600);
    return () => clearTimeout(t);
  }, [visible]);

  // ── 書物の周囲を漂う光の粒子 ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 320;
    canvas.height = 320;

    interface Orb {
      angle: number;
      dist: number;
      speed: number;
      size: number;
      opacity: number;
      phase: number;
      hue: number;
    }

    const orbs: Orb[] = Array.from({ length: 18 }, (_, i) => ({
      angle: (i / 18) * Math.PI * 2,
      dist: 90 + Math.random() * 50,
      speed: (0.004 + Math.random() * 0.008) * (Math.random() < 0.5 ? 1 : -1),
      size: 1 + Math.random() * 2.5,
      opacity: 0.2 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2,
      hue: 38 + Math.random() * 22,
    }));

    let t = 0;

    function draw() {
      t++;
      ctx.clearRect(0, 0, 320, 320);

      if (!visible || !glowing) {
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      const cx = 160;
      const cy = 160;
      const hoverMult = hover ? 1.6 : 1;

      orbs.forEach((orb) => {
        orb.angle += orb.speed;
        orb.phase += 0.02;

        const pulse = Math.sin(orb.phase) * 0.4 + 0.6;
        const alpha = orb.opacity * pulse * hoverMult;

        const x = cx + Math.cos(orb.angle) * orb.dist;
        const y = cy + Math.sin(orb.angle) * orb.dist * 0.6;

        // 光の軌跡
        const trailLen = 8;
        for (let j = 0; j < trailLen; j++) {
          const ta = orb.angle - orb.speed * j * 3;
          const tx = cx + Math.cos(ta) * orb.dist;
          const ty = cy + Math.sin(ta) * orb.dist * 0.6;
          const ta2 = (j / trailLen);
          ctx.beginPath();
          ctx.arc(tx, ty, orb.size * (1 - ta2) * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${orb.hue}, 80%, 75%, ${alpha * (1 - ta2) * 0.3})`;
          ctx.fill();
        }

        // 光球本体
        const grd = ctx.createRadialGradient(x, y, 0, x, y, orb.size * 3);
        grd.addColorStop(0, `hsla(${orb.hue}, 90%, 92%, ${alpha})`);
        grd.addColorStop(0.5, `hsla(${orb.hue}, 80%, 72%, ${alpha * 0.4})`);
        grd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(x, y, orb.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, orb.size * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${orb.hue}, 100%, 96%, ${alpha * 0.9})`;
        ctx.fill();
      });

      // 中央の書物のグロー（ホバー時に強まる）
      if (hover) {
        const centerGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, 120);
        centerGrd.addColorStop(0, "rgba(255,220,120,0.12)");
        centerGrd.addColorStop(0.5, "rgba(255,200,80,0.05)");
        centerGrd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(cx, cy, 120, 0, Math.PI * 2);
        ctx.fillStyle = centerGrd;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [visible, glowing, hover]);

  return (
    <div
      className="flex flex-col items-center cursor-pointer select-none relative"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.92)",
        transition: "opacity 2s ease, transform 2s ease",
      }}
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* 光の粒子 Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute pointer-events-none"
        style={{
          width: "320px",
          height: "320px",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 0,
          opacity: glowing ? 1 : 0,
          transition: "opacity 2s ease",
        }}
      />

      {/* 書物本体 */}
      <div
        className="relative"
        style={{
          zIndex: 1,
          filter: hover
            ? "drop-shadow(0 0 50px rgba(255,215,80,0.6)) drop-shadow(0 0 100px rgba(255,190,30,0.35)) drop-shadow(0 0 160px rgba(255,160,0,0.15))"
            : glowing
            ? "drop-shadow(0 0 22px rgba(255,200,60,0.35)) drop-shadow(0 0 50px rgba(255,170,20,0.18))"
            : "none",
          transition: "filter 0.8s ease",
        }}
      >
        <div
          style={{
            width: "170px",
            height: "215px",
            transform: hover ? "scale(1.07) rotateY(-10deg)" : "scale(1) rotateY(0deg)",
            transition: "transform 0.7s ease",
            transformStyle: "preserve-3d",
            position: "relative",
          }}
        >
          {/* ── 表紙 ── */}
          <div
            className="absolute inset-0 rounded-sm overflow-hidden"
            style={{
              background: "linear-gradient(145deg, #1c1000 0%, #2e1a00 25%, #1e1100 55%, #0e0600 100%)",
              border: "1px solid rgba(201,168,76,0.35)",
              boxShadow: "inset 0 0 40px rgba(0,0,0,0.85), 2px 0 10px rgba(0,0,0,0.7)",
            }}
          >
            {/* 外枠装飾（二重） */}
            <div
              className="absolute"
              style={{
                inset: "10px",
                border: "1px solid rgba(201,168,76,0.22)",
                borderRadius: "1px",
              }}
            />
            <div
              className="absolute"
              style={{
                inset: "16px",
                border: "1px solid rgba(201,168,76,0.1)",
                borderRadius: "1px",
              }}
            />

            {/* 四隅の装飾 */}
            {[
              { top: "10px", left: "10px" },
              { top: "10px", right: "10px" },
              { bottom: "10px", left: "10px" },
              { bottom: "10px", right: "10px" },
            ].map((pos, i) => (
              <div
                key={i}
                className="absolute w-3 h-3"
                style={{
                  ...pos,
                  borderTop: i < 2 ? "1px solid rgba(201,168,76,0.35)" : "none",
                  borderBottom: i >= 2 ? "1px solid rgba(201,168,76,0.35)" : "none",
                  borderLeft: i % 2 === 0 ? "1px solid rgba(201,168,76,0.35)" : "none",
                  borderRight: i % 2 === 1 ? "1px solid rgba(201,168,76,0.35)" : "none",
                }}
              />
            ))}

            {/* 中央：天使のシンボル（光輪＋翼）*/}
            <div
              className="absolute left-1/2 -translate-x-1/2"
              style={{ top: "30px" }}
            >
              {/* 光輪 */}
              <div
                className="mx-auto mb-1"
                style={{
                  width: "32px",
                  height: "10px",
                  borderRadius: "50%",
                  border: "1px solid rgba(201,168,76,0.35)",
                  opacity: hover ? 1 : 0.6,
                  boxShadow: hover ? "0 0 10px rgba(255,200,60,0.5)" : "none",
                  transition: "box-shadow 0.5s ease, opacity 0.5s ease",
                }}
              />
              {/* 十字 */}
              <div className="flex flex-col items-center">
                <div style={{ width: "1px", height: "14px", background: "rgba(201,168,76,0.4)" }} />
                <div className="flex items-center gap-0">
                  <div style={{ width: "14px", height: "1px", background: "rgba(201,168,76,0.4)" }} />
                  <div
                    style={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      background: "rgba(255,215,80,0.7)",
                      margin: "0 2px",
                      boxShadow: hover ? "0 0 8px rgba(255,200,60,0.9), 0 0 16px rgba(255,180,30,0.5)" : "0 0 4px rgba(255,200,60,0.4)",
                      transition: "box-shadow 0.5s ease",
                    }}
                  />
                  <div style={{ width: "14px", height: "1px", background: "rgba(201,168,76,0.4)" }} />
                </div>
                <div style={{ width: "1px", height: "12px", background: "rgba(201,168,76,0.2)" }} />
              </div>
            </div>

            {/* タイトル */}
            <div
              className="absolute left-1/2 -translate-x-1/2"
              style={{ top: "102px", width: "120px", textAlign: "center" }}
            >
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "rgba(201,168,76,0.65)",
                  fontSize: "8.5px",
                  letterSpacing: "0.4em",
                  whiteSpace: "nowrap",
                  textShadow: hover ? "0 0 12px rgba(255,200,60,0.5)" : "none",
                  transition: "text-shadow 0.5s ease",
                }}
              >
                ZEN GENESIS
              </div>
              <div
                className="mx-auto mt-1.5"
                style={{
                  width: hover ? "50px" : "30px",
                  height: "1px",
                  background: "rgba(201,168,76,0.3)",
                  transition: "width 0.6s ease",
                }}
              />
              <div
                className="mt-1.5"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "rgba(201,168,76,0.35)",
                  fontSize: "7px",
                  letterSpacing: "0.55em",
                  whiteSpace: "nowrap",
                }}
              >
                DIVINE CODEX
              </div>
            </div>

            {/* 神聖幾何学（中央下部の六芒星風） */}
            <div
              className="absolute left-1/2 -translate-x-1/2"
              style={{ bottom: "32px" }}
            >
              <div className="relative flex items-center justify-center" style={{ width: "28px", height: "28px" }}>
                {/* 外円 */}
                <div
                  style={{
                    position: "absolute",
                    width: "26px",
                    height: "26px",
                    borderRadius: "50%",
                    border: "1px solid rgba(201,168,76,0.18)",
                    animation: "bookSymbolRotate 12s linear infinite",
                  }}
                />
                {/* 内円 */}
                <div
                  style={{
                    position: "absolute",
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    border: "1px dashed rgba(201,168,76,0.12)",
                    animation: "bookSymbolRotate 8s linear infinite reverse",
                  }}
                />
                {/* 中心点 */}
                <div
                  style={{
                    width: "3px",
                    height: "3px",
                    borderRadius: "50%",
                    background: "rgba(201,168,76,0.45)",
                    boxShadow: hover ? "0 0 6px rgba(255,200,60,0.6)" : "none",
                  }}
                />
              </div>
            </div>

            {/* ページ数の装飾 */}
            <div
              className="absolute bottom-3 right-3"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "rgba(201,168,76,0.15)",
                fontSize: "6px",
                letterSpacing: "0.3em",
              }}
            >
              XII
            </div>
          </div>

          {/* 背表紙 */}
          <div
            className="absolute top-0 left-0 bottom-0 rounded-l-sm"
            style={{
              width: "14px",
              background: "linear-gradient(to right, #080400, #1c1000)",
              borderRight: "1px solid rgba(201,168,76,0.18)",
              transform: "translateX(-12px)",
            }}
          >
            <div
              className="absolute left-1/2 -translate-x-1/2"
              style={{ top: "50%", transform: "translate(-50%, -50%) rotate(-90deg)", whiteSpace: "nowrap" }}
            >
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "rgba(201,168,76,0.2)",
                  fontSize: "5px",
                  letterSpacing: "0.4em",
                }}
              >
                DIVINE CODEX
              </span>
            </div>
          </div>

          {/* ページ断面（右側） */}
          <div
            className="absolute top-1 right-0 bottom-1"
            style={{
              width: "7px",
              background: "repeating-linear-gradient(0deg, rgba(240,230,200,0.07) 0px, rgba(240,230,200,0.03) 1px, transparent 1px, transparent 3px)",
              transform: "translateX(6px) skewY(-0.5deg)",
            }}
          />

          {/* ホバー時：表紙が微かに開く感じ */}
          {hover && (
            <div
              className="absolute inset-0 rounded-sm pointer-events-none"
              style={{
                background: "linear-gradient(to right, rgba(255,200,60,0.04) 0%, transparent 40%)",
                animation: "bookOpenHover 0.6s ease forwards",
              }}
            />
          )}
        </div>
      </div>

      {/* 下部テキスト */}
      <div
        className="mt-10 text-center"
        style={{
          opacity: visible ? 1 : 0,
          transition: "opacity 2s ease 1.2s",
          position: "relative",
          zIndex: 1,
        }}
      >
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: hover ? "rgba(255,215,80,0.7)" : "rgba(201,168,76,0.35)",
            fontSize: "9.5px",
            letterSpacing: "0.55em",
            transition: "color 0.5s ease",
            textShadow: hover ? "0 0 20px rgba(255,200,60,0.5)" : "none",
          }}
        >
          {hover ? "OPEN THE CODEX" : "TAP TO RECEIVE"}
        </p>
        <div
          className="mt-2 mx-auto"
          style={{
            width: hover ? "64px" : "28px",
            height: "1px",
            background: hover ? "rgba(255,215,80,0.4)" : "rgba(201,168,76,0.25)",
            transition: "width 0.6s ease, background 0.5s ease",
          }}
        />
      </div>

      <style>{`
        @keyframes bookSymbolRotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes bookOpenHover {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
